<!-- eslint-disable vue/no-v-html -->
<script lang="ts">
import { defineComponent } from 'vue';
import PhoneNumberField from './fields/PhoneNumberField.vue';
import AgeVerificationField from './fields/AgeVerificationField.vue';
import CheckboxField from './fields/CheckboxField.vue';
import HiddenField from './fields/HiddenField.vue';
import SingleSelectField from './fields/SingleSelectField.vue';
import SubmitField from './fields/SubmitField.vue';
import TextareaField from './fields/TextareaField.vue';
import TextInputField from './fields/TextInputField.vue';
import UnsupportedField from './fields/UnsupportedField.vue';
import ReadonlyDescriptionField from './fields/ReadonlyDescriptionField.vue';
import RecaptchaField from './fields/RecaptchaField.vue';
import CoordinatorProvider from './coordinator/CoordinatorProvider';
import AutoCompleteField from './fields/AutoCompleteField.vue';
import MultiSelectField from './fields/MultiSelectField.vue';
import PortableText from '../../../../components/Atoms/PortableText/PortableText.vue';

export default defineComponent({
  components: {
    CheckboxField,
    HiddenField,
    ReadonlyDescriptionField,
    RecaptchaField,
    SingleSelectField,
    SubmitField,
    TextareaField,
    TextInputField,
    AutoCompleteField,
    PhoneNumberField,
    AgeVerificationField,
    UnsupportedField,
    MultiSelectField,
    PortableText,
  },
  setup() {
    return {
      ...CoordinatorProvider.get().formFacet(),
    };
  },
});

// Note on the template, need to remove the `id="hekate-form-fort-container"`
</script>

<template>
  <div
    v-if="!formDefinition.isHidden"
    id="hekate-form-fort-container"
    class="hekate-form-fort-container"
  >
    <div
      v-if="isShowThankYou"
      class="thank-you-message"
    >
      Thank you for your submission.
    </div>

    <div
      v-if="isShowSuccessMessage"
      class="success-message"
    >
      <div
        v-if="successMessagePortableText?.buttonPortableText"
        id="formfort-success-message"
      >
        <PortableText :portable-data="successMessagePortableText.buttonPortableText" />
      </div>
    </div>

    <div
      v-if="isShow409Error"
      class="form-top-error"
    >
      <p>There was a problem registering your email address.</p>
      <p>
        Already have an account?<br /><a href="https://cad.onshape.com/signin"
          >Sign in or reset your password</a
        >.
      </p>
      <p>
        Have a free account and looking to try Pro? <br />
        <a href="https://www.onshape.com/request-a-trial-of-onshape-professional?hsLang=en"
          >Request your pro trial for free users here</a
        >.
      </p>
      <p>
        <a
          href="#"
          @click="clear409Error"
          >Try again</a
        >
        with a different email address.
      </p>
      <p>
        You can also <a href="https://www.onshape.com/contact?hsLang=en">contact us</a> for
        assistance.
      </p>
    </div>

    <form
      v-if="isShowForm"
      ref="formElement"
      :class="{
        'form-is-invalid': formDefinition.errorMessages.length > 0,
        'form-is-submitting': isFormSubmitting,
      }"
      :form-id="formDefinition.id"
      :form-name="formDefinition.name"
      @submit.prevent="onSubmitForm"
    >
      <div class="form-is-submitting-overlay">
        <div class="form-loading-spinner">
          <div class="lds-ring">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      </div>

      <div
        v-for="(err, ii) in formDefinition.errorMessages"
        :key="`form-error-${ii}`"
        class="form-top-error"
      >
        <p>
          {{ err.message }}
        </p>
      </div>

      <textarea
        v-if="supportEmailText"
        v-model="supportEmailText"
        class="support-email-text"
      />

      <component
        :is="field.componentName"
        v-for="field in formDefinition.displayFields"
        :key="`form-field-${field.id}-${field.componentKey}`"
        v-model="field.val"
        :class="{ [`form-field-id-${field.id}-form-id-${formDefinition.id}`]: true }"
        :field-name="field.htmlName"
        :field="field"
        :form-id="formDefinition.id"
        @blur="onFieldBlur"
        @focus="onFieldFocus"
      />
    </form>
  </div>
</template>

<style lang="scss">
@use '../../style/vars.scss' as vars;
@use '../../style/form-151-email-preferences.scss' as emailPreferencesStyles;
@use '../../style/form-60-startup-form.scss' as startupFormStyles;

// All css must go under this selector.
#hekate-form-fort-container {
  margin-left: 8px;
  margin-right: 8px;

  .success-message,
  .thank-you-message {
    font-size: 1.4em;
    font-weight: bold;
  }

  .support-email-text {
    width: 98%;
    height: 300px !important;
    margin: 8px 4px 8px 4px;
    border: 1px solid vars.$color-error;
  }

  .form-top-error {
    color: vars.$color-error;
    font-style: oblique;
    width: 100%;
    font-size: 16px;
    border: 1px solid vars.$color-error;
    background: vars.$color-error-background;
    padding: 10px 0 10px 0;
    p {
      padding: 4px;
      text-align: center;
    }
  }

  form {
    scroll-margin: 60px;

    margin: 4px;
    padding: 8px;
    border: 2px solid transparent;

    .form-is-submitting-overlay {
      display: none;
    }

    .form-fields-box {
      margin: 0;
      padding: 0;
    }

    &.form-is-submitting {
      position: relative;
      .form-is-submitting-overlay {
        background: rgba(33, 33, 33, 0.2);
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 10;
        display: flex;
        justify-content: center;
        padding-top: 33px;
        border-radius: 4px;

        /** see: https://loading.io/css/  */
        .form-loading-spinner {
          .lds-ring {
            display: inline-block;
            position: relative;
            width: 90px;
            height: 90px;
          }
          .lds-ring div {
            box-sizing: border-box;
            display: block;
            position: absolute;
            width: 104px;
            height: 104px;
            margin: 8px;
            border: 3px solid vars.$color-onshape-green;
            border-radius: 50%;
            animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
            border-color: vars.$color-onshape-green transparent transparent transparent;
          }
          .lds-ring div:nth-child(1) {
            animation-delay: -0.45s;
          }
          .lds-ring div:nth-child(2) {
            animation-delay: -0.3s;
          }
          .lds-ring div:nth-child(3) {
            animation-delay: -0.15s;
          }
          @keyframes lds-ring {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        }
      }
    }

    &.form-is-invalid {
      border: 2px solid vars.$color-input-border;
    }

    input,
    select {
      -webkit-font-smoothing: antialiased;
      -webkit-box-direction: normal;
      box-sizing: border-box;
      height: 48px;
      font-size: 17px;
      padding: 12px;
      border: 1px solid vars.$color-input-border;
      margin-bottom: 12px;
      width: 100%;
    }

    input,
    select,
    textarea {
      background: #fafafa;
      background: vars.$color-input-background;
    }

    input[type='checkbox'] {
      height: 22px;
      width: 22px;
      padding: 12px;

      &:hover {
        cursor: pointer;
      }
    }

    .field-content-is-validated {
      input,
      select,
      textarea {
        border-right: 4px solid vars.$color-onshape-green;
      }
    }

    .field-content-is-errored {
      input,
      select,
      textarea {
        border: 1px solid vars.$color-error;
        margin-bottom: 0px;
      }
    }

    label {
      display: block;
      &.flex {
        display: flex;
      }

      &:hover {
        cursor: pointer;
      }
    }

    .required-indicator {
      color: vars.$color-error;
    }

    .field-container {
      font-family: 'Raleway', 'Arial', sans-serif;
      font-weight: 400;
      line-height: 1.8;
      text-rendering: optimizeLegibility;
      -webkit-font-smoothing: antialiased;
      -webkit-box-direction: normal;
      color: #2f4265;
      font-size: 17px;
      box-sizing: border-box;
    }
  }
}
</style>
